/* eslint-disable sonarjs/prefer-immediate-return */
import type { NewContentBlock } from '../stores/content-block-store'
import type { ContentBlocksAndAdSlotMarkers, DeviceData } from '../types/content-block-types'

// Helper functions to get the previous and next enabled block
function getPreviousEnabledBlock (index: number, blocks: NewContentBlock<ContentBlocksAndAdSlotMarkers>[]): NewContentBlock<ContentBlocksAndAdSlotMarkers> | undefined {
  for (let i = index - 1; i >= 0; i--) {
    if (blocks[i]?.enabled) return blocks[i]
  }
  return undefined
}

export function getNextEnabledBlock (index: number, blocks: NewContentBlock<ContentBlocksAndAdSlotMarkers>[]): NewContentBlock<ContentBlocksAndAdSlotMarkers> | undefined {
  for (let i = index + 1; i < blocks.length; i++) {
    if (blocks[i]?.enabled) return blocks[i]
  }
  return undefined
}

/**
 * Gets the combined height of all consecutive text blocks that come after the given block
 * @param from - The block to start counting from
 * @param blocks - The array of all content blocks
 * @returns The combined height of all consecutive text blocks after the given block
 */
export function getCombinedHeightOfForwardTextBlocks (from: NewContentBlock<ContentBlocksAndAdSlotMarkers>, blocks: NewContentBlock<ContentBlocksAndAdSlotMarkers>[]): number {
  const consecutiveTextBlocks: NewContentBlock<ContentBlocksAndAdSlotMarkers>[] = []
  const allEnabledBlocks = blocks.filter(block => block.enabled)
  const fromIndex = allEnabledBlocks.findIndex(block => block.id === from.id)
  for (let i = fromIndex; i < allEnabledBlocks.length; i++) {
    if (allEnabledBlocks[i]?.type !== 'text') {
      break
    }
    consecutiveTextBlocks.push(allEnabledBlocks[i]!)
  }

  return consecutiveTextBlocks.reduce((acc, block) => {
    return acc + block.dimensions.innerHeight
  }, 0)
}

/**
 * Utility functions to determine if content blocks should float based on their position
 * and relationship to other blocks in the content flow
 */
export const checkContentBlockFloat = {
  /**
   * Checks if a quote block should float
   * A quote block should float if:
   * - It's followed by a text block
   * - It's not preceded by a rectangle ad (a rectangle ad should always float instead)
   *
   * @param index - The index of the current block in the content blocks array
   * @param contentBlocks - Array of content blocks
   * @returns boolean indicating if the quote block should float
   */
  checkQuoteFloat: (
    index: number,
    contentBlocks: NewContentBlock<ContentBlocksAndAdSlotMarkers>[]
  ): boolean => {
    const self = contentBlocks[index]
    const next = getNextEnabledBlock(index, contentBlocks)
    if (next?.type !== 'text' || !self) return false

    const quoteHeight = self.dimensions.innerHeight
    const combinedTextHeight = getCombinedHeightOfForwardTextBlocks(next, contentBlocks)

    // If the quote is taller than the combined height of the text blocks, then it should not float
    if (quoteHeight > combinedTextHeight) return false

    // Check if preceded by a rectangle ad, in which case the quote should not float
    const prev = getPreviousEnabledBlock(index, contentBlocks)
    // eslint-disable-next-line sonarjs/prefer-single-boolean-return -- easier to read this way
    if (prev?.type === '__injected_ad__' && prev.data?.adSlotMarker?.adType === 'rectangle') {
      return false
    }

    return true
  },

  /**
   * Checks if an ad block should float
   * An ad block should float if:
   * - It's of type rectangle
   * - The next enabled block is of type text OR
   * - The next enabled block is a quote followed by a text block OR
   * - If the next block is a quote and the previous enabled block is a text block then the ad should not float
   *
   * @param index - The index of the current block in the content blocks array
   * @param contentBlocks - Array of content blocks
   * @returns boolean indicating if the ad block should float
   */
  checkAdFloat: (
    index: number,
    contentBlocks: NewContentBlock<ContentBlocksAndAdSlotMarkers>[]
  ): boolean => {
    const self = contentBlocks[index]
    if (!self || !self.enabled) return false
    const isRectangleAd = self?.type === '__injected_ad__' && self?.data?.adSlotMarker?.adType === 'rectangle'
    if (!isRectangleAd) return false

    const next = getNextEnabledBlock(index, contentBlocks)

    // Ad floats if followed directly by a text block
    if (next?.type === 'text') return true

    // Ad also floats if followed by a quote that's followed by a text block
    // or if the ad is preceded by a text block
    if (next?.type === 'quote') {
      // Check if the previous enabled block is a text block
      const prev = getPreviousEnabledBlock(index, contentBlocks)
      if (prev?.type !== 'text') return false

      const nextIndex = contentBlocks.findIndex(block => block.id === next.id)
      const afterQuote = getNextEnabledBlock(nextIndex, contentBlocks)
      return afterQuote?.type === 'text' || false
    }

    return false
  }
}

export const enhanceWithFloat = (blocks: NewContentBlock<ContentBlocksAndAdSlotMarkers>[], deviceData?: DeviceData | null) => {
  return blocks.map((block, index) => {
    if (block.type === 'quote') {
      block.shouldFloat = deviceData ? checkContentBlockFloat.checkQuoteFloat(index, blocks) : false
    } else if (block.type === '__injected_ad__') {
      block.shouldFloat = deviceData ? checkContentBlockFloat.checkAdFloat(index, blocks) : false
    }
    return block
  })
}
